import { initPhoneInput } from '../phoneInput.js';
import { regionsForCountry } from '../taxRates.js';
import { withSubmitGuard } from '../submitGuard.js';

import { esc } from '../escape.js';
import { PLACE_TYPES, OTHER_KINDS, suggestedSetup, cleanHidden } from '../homeLayout.js';

const STEPS = 5;
const STEP_PLACE = 1, STEP_FEATURES = 2, STEP_ADDRESS = 3, STEP_PHONE = 4;

// Step 2 card icons (stroke paths), one per type of place.
const PLACE_ICONS = {
  cafe:       '<path d="M17 8h1a4 4 0 0 1 0 8h-1"/><path d="M3 8h14v9a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4Z"/><path d="M6 2v3M10 2v3M14 2v3"/>',
  restaurant: '<path d="M3 2v7c0 1.1.9 2 2 2h4a2 2 0 0 0 2-2V2"/><path d="M7 2v20"/><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7"/>',
  bakery:     '<path d="M4 15a8 8 0 0 1 16 0v4H4Z"/><path d="M9 11V9M12 10V7M15 11V9"/>',
  food_truck: '<path d="M3 7h11v10H3Z"/><path d="M14 10h4l3 3v4h-7"/><circle cx="7" cy="18" r="2"/><circle cx="17" cy="18" r="2"/>',
  bar:        '<path d="M8 22h8"/><path d="M12 15v7"/><path d="M5 3h14l-7 12Z"/>',
  other:      '<path d="M12 5v14M5 12h14"/>',
};

// Step 3 rows. `hide` = the home shortcut a row shows or hides; rows without
// it are checkout steps (their own setting).
const FEATURES = [
  { id: 'orderType', label: 'Dine in / Takeout', hint: 'Choose one at checkout' },
  { id: 'tips',      label: 'Tips',              hint: 'Tip buttons at checkout' },
  { id: 'tables',    label: 'Tables',            hint: 'Know where each order goes' },
  { id: 'kds',       label: 'Queue Screen',      hint: 'Orders on a screen in the kitchen', hide: 'kds' },
  { id: 'schedule',  label: 'Staff schedule',    hint: 'Build the week, swaps, time off', hide: 'schedule' },
  { id: 'loyalty',   label: 'Loyalty points',    hint: 'Regulars earn rewards', hide: 'loyalty', pro: true },
  { id: 'inventory', label: 'Inventory',         hint: 'Stock counts, suppliers, purchase orders', hide: 'inventory', pro: true },
];

const CHECK_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M20 6 9 17l-5-5"/></svg>';

class OnboardingView {
  _overlay   = document.getElementById('onboardingOverlay');
  _errorEl   = document.getElementById('onboardingError');
  _nextBtn   = document.getElementById('onboardingNextBtn');
  _backBtn   = document.getElementById('onboardingBackBtn');
  _dots      = [...document.querySelectorAll('.ob-dot')];
  _steps     = [...document.querySelectorAll('.ob-step')];
  _current   = 0;
  _phoneIti  = null;
  _place     = null;   // PLACE_TYPES id picked in step 2
  _setup     = null;   // step 3 choices, seeded from suggestedSetup(_place)

  _detectCountry() {
    const tz   = Intl.DateTimeFormat().resolvedOptions().timeZone ?? '';
    const lang = (navigator.language ?? '').toUpperCase();
    if (lang.endsWith('-US') || (tz.startsWith('America/') && !lang.endsWith('-CA') && !lang.endsWith('-MX') && !lang.endsWith('-BR'))) return 'us';
    if (lang.endsWith('-PH') || tz === 'Asia/Manila') return 'ph';
    if (lang.endsWith('-CA') || tz.startsWith('America/Toronto') || tz.startsWith('America/Vancouver') || tz.startsWith('America/Winnipeg')) return 'ca';
    if (lang.endsWith('-GB') || tz === 'Europe/London') return 'gb';
    if (lang.endsWith('-AU') || tz.startsWith('Australia/')) return 'au';
    if (lang.endsWith('-SG') || tz === 'Asia/Singapore') return 'sg';
    if (lang.endsWith('-MY') || tz === 'Asia/Kuala_Lumpur') return 'my';
    if (lang.endsWith('-IN') || tz.startsWith('Asia/Kolkata')) return 'in';
    if (lang.endsWith('-NZ') || tz.startsWith('Pacific/Auckland')) return 'nz';
    if (lang.endsWith('-AE') || tz === 'Asia/Dubai') return 'ae';
    if (lang.endsWith('-JP') || tz === 'Asia/Tokyo') return 'jp';
    return 'us';
  }

  show() {
    this._current   = 0;
    this._steps.forEach((s, i) => s.classList.toggle('ob-step--hidden', i !== 0));
    this._updateUI();
    this._overlay.classList.remove('hidden');
    this._focusCurrent();

    const detected = this._detectCountry();

    if (!this._phoneIti) {
      this._phoneIti = initPhoneInput('onboardingPhone');
    }
    if (this._phoneIti) this._phoneIti.setCountry(detected);

    const countryEl = document.getElementById('onboardingCountry');
    if (countryEl && !countryEl.value) countryEl.value = detected.toUpperCase();

    this._place = null;
    this._setup = null;
    this._showDonePanel(false);
    this._renderPlaces();
    this._renderOtherKinds();
    this._renderFeatures();

    // Show the right Province/State control (constrained dropdown for US/CA, else text).
    this._syncStateField(countryEl?.value ?? detected.toUpperCase());

  }

  hide() {
    this._overlay.classList.add('hidden');
  }

  showError(msg) { this._errorEl.textContent = msg; }
  clearError()   { this._errorEl.textContent = ''; }

  setLoading(bool) {
    this._nextBtn.disabled    = bool;
    this._nextBtn.textContent = bool ? 'Saving…' : this._nextLabel();
  }

  _nextLabel() {
    return this._current === STEPS - 1 ? 'Get Started' : 'Next';
  }

  _updateUI() {
    this._dots.forEach((d, i) => d.classList.toggle('ob-dot--active', i === this._current));
    this._backBtn.classList.toggle('hidden', this._current === 0);
    // The card widens for the two choice steps (cards and checklist).
    this._overlay.querySelector('.auth-card')?.classList.toggle('auth-card--wide', this._current === STEP_PLACE || this._current === STEP_FEATURES);
    this._nextBtn.textContent = this._nextLabel();
  }

  // ── Step 2: type of place ──────────────────────────────────────────────────

  _renderPlaces() {
    const grid = document.getElementById('obPlaceGrid');
    if (!grid) return;
    grid.innerHTML = PLACE_TYPES.map((p) => {
      const on = p.id === this._place;
      return `
        <button type="button" class="ob-place${on ? ' ob-place--on' : ''}" role="radio" aria-checked="${on}" data-place="${p.id}">
          <span class="ob-place-icon" aria-hidden="true"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">${PLACE_ICONS[p.id]}</svg></span>
          <span class="ob-place-text"><span class="ob-place-name">${p.label}</span><span class="ob-place-hint">${p.hint}</span></span>
        </button>`;
    }).join('');
    document.getElementById('obOtherKindField')?.classList.toggle('hidden', this._place !== 'other');
  }

  _renderOtherKinds() {
    const sel = document.getElementById('obOtherKind');
    if (!sel || sel.options.length > 1) return;
    OTHER_KINDS.forEach((k) => {
      const o = document.createElement('option');
      o.value = k.id;
      o.textContent = k.label;
      sel.appendChild(o);
    });
  }

  // Picking a different type re-ticks step 3 from that type's suggestions.
  _pickPlace(id) {
    if (id === this._place) return;
    this._place = id;
    this._setup = this._seedSetup(id);
    this._renderPlaces();
    this._renderFeatures();
    this.clearError();
  }

  // Step 2's title uses the name from step 1.
  _syncPlaceTitle() {
    const name = document.getElementById('onboardingBizName')?.value.trim();
    const el = document.getElementById('obPlaceTitle');
    if (el) el.textContent = name ? `What kind of place is ${name}?` : 'What kind of place is it?';
  }

  // ── Step 3: features ───────────────────────────────────────────────────────

  _seedSetup(place) {
    const s = suggestedSetup(place);
    return {
      orderType: s.orderTypeEnabled,
      tips: s.tipsEnabled,
      tables: s.tableService !== 'off',
      tableMode: s.tableService === 'full' ? 'full' : 'numbers',
      waitlist: s.tableService === 'full' ? s.waitlistEnabled : true,
      reservations: s.reservationsEnabled,
      // Shortcuts step 3 doesn't ask about keep the type's suggestion.
      hidden: s.homeHidden,
      ...Object.fromEntries(FEATURES.filter((f) => f.hide).map((f) => [f.id, !s.homeHidden.includes(f.hide)])),
    };
  }

  _renderFeatures() {
    const list = document.getElementById('obFeatureList');
    if (!list) return;
    if (!this._setup) this._setup = this._seedSetup(this._place ?? 'cafe');
    const s = this._setup;
    const placeLabel = PLACE_TYPES.find((p) => p.id === this._place)?.label;
    const hint = document.getElementById('obFeatureHint');
    if (hint) {
      hint.textContent = (placeLabel && this._place !== 'other' ? `We ticked what a ${placeLabel.split(' /')[0].toLowerCase()} usually uses. ` : '')
        + 'This only sets up your home screen. Every feature stays available to you, and you can change it anytime in Settings.';
    }
    list.innerHTML = FEATURES.map((f) => {
      const on = !!s[f.id];
      const tag = f.pro ? '<span class="ob-tag ob-tag--pro">Pro</span>' : '';
      const sub = f.id === 'tables' && on ? `
        <div class="ob-sub">
          <div class="ob-seg" role="radiogroup" aria-label="How tables work">
            <button type="button" class="ob-seg-btn${s.tableMode === 'numbers' ? ' ob-seg-btn--on' : ''}" role="radio" aria-checked="${s.tableMode === 'numbers'}" data-table-mode="numbers">They pay first, then sit</button>
            <button type="button" class="ob-seg-btn${s.tableMode === 'full' ? ' ob-seg-btn--on' : ''}" role="radio" aria-checked="${s.tableMode === 'full'}" data-table-mode="full">We serve at the table</button>
          </div>
          <p class="ob-sub-hint">${s.tableMode === 'full'
            ? 'Floor plan, open tabs, send to the kitchen as they order, pay at the end.'
            : 'Checkout asks for a table number so you can bring the order over.'}</p>
          ${s.tableMode === 'full' ? `
          <div class="ob-sub-checks">
            <label><input type="checkbox" data-sub="waitlist"${s.waitlist ? ' checked' : ''}> Waitlist</label>
            <label><input type="checkbox" data-sub="reservations"${s.reservations ? ' checked' : ''}> Reservations</label>
          </div>` : ''}
        </div>` : '';
      return `
        <div class="ob-feature${on ? ' ob-feature--on' : ''}">
          <button type="button" class="ob-feature-row" role="checkbox" aria-checked="${on}" data-feature="${f.id}">
            <span class="ob-check" aria-hidden="true">${CHECK_SVG}</span>
            <span class="ob-feature-text"><span class="ob-feature-name">${f.label}</span><span class="ob-feature-hint">${f.hint}</span></span>
            ${tag}
          </button>${sub}
        </div>`;
    }).join('') + '<p class="ob-fine">Pro features are included in your 7-day free trial.</p>';
  }

  // What the choices mean in settings terms (keys of state.settings).
  _result() {
    const s = this._setup ?? this._seedSetup(this._place ?? 'other');
    let hidden = [...s.hidden];
    for (const f of FEATURES.filter((x) => x.hide)) {
      hidden = s[f.id] ? hidden.filter((k) => k !== f.hide) : [...hidden, f.hide];
    }
    const tableService = s.tables ? s.tableMode : 'off';
    return {
      placeType: this._place,
      orderTypeEnabled: !!s.orderType,
      tipsEnabled: !!s.tips,
      tableService,
      waitlistEnabled: tableService === 'full' && !!s.waitlist,
      reservationsEnabled: tableService === 'full' && !!s.reservations,
      homeHidden: cleanHidden(hidden),
    };
  }

  // ── Done screen ────────────────────────────────────────────────────────────

  _showDonePanel(show) {
    const card = this._overlay.querySelector('.auth-card');
    card?.classList.toggle('ob-card--done', show);
    if (show) card?.classList.add('auth-card--wide');
    document.getElementById('onboardingDone')?.classList.toggle('hidden', !show);
  }

  // Lists what's on the home screen now, from the choices just saved.
  showDone() {
    const s = this._setup ?? {};
    const r = this._result();
    const rows = FEATURES.map((f) => {
      const on = !!s[f.id];
      let note = f.hint;
      if (f.id === 'tables' && on) note = r.tableService === 'full' ? 'Floor plan and open tabs' : 'Table number at checkout';
      if (!on) note = f.hide ? 'Hidden from home, still available' : 'Off, turn it on in Settings';
      return `
        <li class="ob-done-row${on ? '' : ' ob-done-row--off'}">
          <span class="ob-done-mark" aria-hidden="true">${on ? CHECK_SVG : '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3" stroke-linecap="round"><path d="M6 12h12"/></svg>'}</span>
          <span class="ob-done-text"><span class="ob-done-name">${esc(f.label)}</span><span class="ob-done-hint">${esc(note)}</span></span>
          <span class="ob-done-state">${on ? 'On' : 'Off'}</span>
        </li>`;
    }).join('');
    const list = document.getElementById('obDoneList');
    if (list) list.innerHTML = rows;
    this._showDonePanel(true);
    setTimeout(() => document.getElementById('onboardingDoneBtn')?.focus(), 150);
  }

  _addHandlerDone(handler) {
    document.getElementById('onboardingDoneBtn')?.addEventListener('click', () => handler());
  }

  // Fill the region dropdown for the given country (e.g. US states, CA provinces).
  // Re-fills when the country changes so US <-> CA switches show the right list.
  _populateRegions(country, regions) {
    const sel = document.getElementById('onboardingStateSelect');
    if (!sel) return;
    const cc = (country ?? '').toUpperCase();
    if (sel.dataset.filledFor === cc) return; // already populated for this country
    sel.innerHTML = '<option value="">Select one</option>';
    regions.forEach(({ code, name }) => {
      const o = document.createElement('option');
      o.value = code;
      o.textContent = name;
      sel.appendChild(o);
    });
    sel.dataset.filledFor = cc;
  }

  // Countries with sub-region tax (US states, CA provinces) get the constrained
  // dropdown so the region can't be mistyped; any other country keeps free text.
  _syncStateField(country) {
    const input = document.getElementById('onboardingProvince');
    const sel   = document.getElementById('onboardingStateSelect');
    if (!input || !sel) return;
    const regions = regionsForCountry(country);
    if (regions) this._populateRegions(country, regions);
    sel.classList.toggle('hidden', !regions);
    input.classList.toggle('hidden', !!regions);
  }

  // Default the phone's country from the address country the user chose in step 3.
  // "OTHER" / a country the phone widget doesn't recognize is left as-is.
  _syncPhoneCountryFromAddress() {
    if (!this._phoneIti) return;
    const cc = document.getElementById('onboardingCountry')?.value;
    if (cc && cc !== 'OTHER') this._phoneIti.setCountry(cc.toLowerCase());
  }

  // The Province / State value, reading whichever control is active for the country.
  _provinceValue() {
    const sel = document.getElementById('onboardingStateSelect');
    if (sel && !sel.classList.contains('hidden')) {
      // Empty value = placeholder still selected -> treat as no input (don't return the
      // placeholder label). Otherwise store the full region name (readable on receipts/
      // address); the tax lookup resolves the name back to a code.
      if (!sel.value) return '';
      return sel.selectedOptions[0]?.textContent.trim() ?? '';
    }
    return document.getElementById('onboardingProvince')?.value.trim() ?? '';
  }

  _focusCurrent() {
    const input = this._steps[this._current]?.querySelector('input, select');
    if (input) setTimeout(() => input.focus(), 200);
  }

  _goTo(index, direction) {

    const outEl = this._steps[this._current];
    const inEl  = this._steps[index];
    const outX  = direction === 'forward' ? '-28px' : '28px';
    const inX   = direction === 'forward' ? '28px'  : '-28px';

    outEl.style.transition = 'opacity 160ms ease, transform 160ms ease';
    outEl.style.opacity    = '0';
    outEl.style.transform  = `translateX(${outX})`;

    setTimeout(() => {
      outEl.classList.add('ob-step--hidden');
      outEl.style.cssText = '';
      inEl.style.opacity   = '0';
      inEl.style.transform = `translateX(${inX})`;
      inEl.classList.remove('ob-step--hidden');
      requestAnimationFrame(() => requestAnimationFrame(() => {
        inEl.style.transition = 'opacity 160ms ease, transform 160ms ease';
        inEl.style.opacity    = '1';
        inEl.style.transform  = 'translateX(0)';
        setTimeout(() => { inEl.style.cssText = ''; }, 170);
      }));
      this._current = index;
      this._updateUI();
      this.clearError();
      this._focusCurrent();
      if (index === STEP_PLACE) this._syncPlaceTitle();
      if (index === STEP_FEATURES) this._renderFeatures();
      // Reaching the phone step: seed its country from the address country.
      if (index === STEP_PHONE) this._syncPhoneCountryFromAddress();
    }, 160);
  }

  _validate() {
    if (this._current === 0) {
      if (!document.getElementById('onboardingBizName')?.value.trim()) {
        this.showError('Please enter your business name.');
        return false;
      }
    }
    if (this._current === STEP_PLACE) {
      if (!this._place) {
        this.showError('Please pick the closest type of place.');
        return false;
      }
      if (this._place === 'other' && !document.getElementById('obOtherKind')?.value) {
        this.showError('Please pick what you sell.');
        return false;
      }
    }
    // Address comes before Phone so the phone's country can default from the
    // chosen address country.
    if (this._current === STEP_ADDRESS) {
      const street   = document.getElementById('onboardingStreet')?.value.trim();
      const city     = document.getElementById('onboardingCity')?.value.trim();
      const province = this._provinceValue();
      const zip      = document.getElementById('onboardingZip')?.value.trim();
      const country  = document.getElementById('onboardingCountry')?.value;
      if (!country)  { this.showError('Please select your country.'); return false; }
      if (!street)   { this.showError('Please enter your street address.'); return false; }
      if (!city)     { this.showError('Please enter your city or municipality.'); return false; }
      if (!province) { this.showError('Please enter your province or state.'); return false; }
      if (!zip)      { this.showError('Please enter your ZIP or postal code.'); return false; }
    }
    if (this._current === STEP_PHONE) {
      const phone = this._phoneIti?.getNumber() ?? '';
      if (!phone) { this.showError('Please enter a phone number.'); return false; }
      if (!this._phoneIti?.isValidNumber()) { this.showError('Please enter a valid phone number.'); return false; }
    }
    return true;
  }

  _addHandlerExit(handler) {
    document.getElementById('onboardingExitLink')?.addEventListener('click', (e) => {
      e.preventDefault();
      handler();
    });
  }

  _addHandlerSubmit(handler) {
    this._nextBtn.addEventListener('click', () => {
      this.clearError();
      if (!this._validate()) return;
      if (this._current < STEPS - 1) {
        this._goTo(this._current + 1, 'forward');
      } else {
        withSubmitGuard(this._nextBtn, () => handler({
          businessName: document.getElementById('onboardingBizName')?.value.trim(),
          // Food places keep business_type 'food_beverage' (the admin panel
          // reads it) with the kind as the industry; others store what they sell.
          businessType: this._place === 'other' ? document.getElementById('obOtherKind')?.value : 'food_beverage',
          industry:     this._place,
          setup:        this._result(),
          phone:        this._phoneIti?.getNumber() ?? '',
          street:       document.getElementById('onboardingStreet')?.value.trim(),
          city:         document.getElementById('onboardingCity')?.value.trim(),
          province:     this._provinceValue(),
          zip:          document.getElementById('onboardingZip')?.value.trim(),
          country:      document.getElementById('onboardingCountry')?.value,
        })); // text/disable owned by the controller's setLoading(); guard adds re-entry protection
      }
    });

    this._backBtn.addEventListener('click', () => {
      if (this._current > 0) this._goTo(this._current - 1, 'back');
    });

    document.getElementById('obPlaceGrid')?.addEventListener('click', (e) => {
      const btn = e.target.closest('[data-place]');
      if (btn) this._pickPlace(btn.dataset.place);
    });
    document.getElementById('obOtherKind')?.addEventListener('change', () => this.clearError());

    // Checklist rows re-render on every change, so put focus back on the
    // control that was used.
    const features = document.getElementById('obFeatureList');
    features?.addEventListener('click', (e) => {
      const row = e.target.closest('[data-feature]');
      const mode = e.target.closest('[data-table-mode]');
      let refocus;
      if (row) {
        this._setup[row.dataset.feature] = !this._setup[row.dataset.feature];
        refocus = `[data-feature="${row.dataset.feature}"]`;
      } else if (mode) {
        this._setup.tableMode = mode.dataset.tableMode;
        refocus = `[data-table-mode="${mode.dataset.tableMode}"]`;
      } else return;
      this._renderFeatures();
      features.querySelector(refocus)?.focus();
    });
    features?.addEventListener('change', (e) => {
      const box = e.target.closest('[data-sub]');
      if (box) this._setup[box.dataset.sub] = box.checked;
    });

    // Country drives whether Province/State is a constrained US dropdown or free text.
    document.getElementById('onboardingCountry')?.addEventListener('change', (e) => {
      this._syncStateField(e.target.value);
    });

    this._overlay.addEventListener('keydown', (e) => {
      // Enter on a card or checklist row picks it (the button's own click), not Next.
      if (e.key === 'Enter' && e.target.closest('.ob-place-grid, .ob-feature-list')) return;
      if (e.key === 'Enter' && !document.getElementById('onboardingDone')?.classList.contains('hidden')) {
        e.preventDefault();
        document.getElementById('onboardingDoneBtn')?.click();
        return;
      }
      if (e.key === 'Enter' && !this._nextBtn.disabled) {
        e.preventDefault();
        this._nextBtn.click();
      }
    });
  }

  // --- Tax suggestion prompt (shown after onboarding when a rate is known) ---

  showTaxPrompt({ label, rate }) {
    const overlay = document.getElementById('taxSuggestOverlay');
    const promptEl = document.getElementById('taxSuggestPrompt');
    if (!overlay || !promptEl) return;
    promptEl.textContent = `Add ${label} (${rate}%) to your receipts?`;
    overlay.classList.remove('hidden');
  }

  hideTaxPrompt() {
    document.getElementById('taxSuggestOverlay')?.classList.add('hidden');
  }

  setTaxLoading(bool) {
    const addBtn = document.getElementById('taxSuggestAddBtn');
    if (addBtn) {
      addBtn.disabled = bool;
      addBtn.textContent = bool ? 'Adding…' : 'Add to receipts';
    }
  }

  _addHandlerTaxPrompt(onAdd, onSkip) {
    document.getElementById('taxSuggestAddBtn')?.addEventListener('click', () => onAdd());
    document.getElementById('taxSuggestSkipBtn')?.addEventListener('click', () => onSkip());
  }
}

export default new OnboardingView();
